import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  ServerStackIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { fetchServiceIPs, addServiceIP, removeServiceIP } from '../api/client';
import Pagination from './Pagination';
import Badge from './ui/Badge';
import { appCategoryTone } from './ui/badgeTones';

const CATEGORIES = ['Microsoft', 'Reserved', 'Custom'];

function formatDate(dateStr) {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
}

export default function ServiceIPManager() {
  const [serviceIPs, setServiceIPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');

  // Add form state
  const [ipAddress, setIpAddress] = useState('');
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState('Custom');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  // Remove confirmation
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Resizable columns: IP Address, Label, Category, Added By, Date, Actions
  const [colWidths, setColWidths] = useState([300, 280, 120, 180, 130, 100]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const loadServiceIPs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchServiceIPs();
      setServiceIPs(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load service IPs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServiceIPs();
  }, []);

  const filteredIPs = useMemo(() => {
    if (!filterQuery.trim()) return serviceIPs;
    const q = filterQuery.toLowerCase();
    return serviceIPs.filter(
      (ip) =>
        (ip.ipAddress && ip.ipAddress.toLowerCase().includes(q)) ||
        (ip.label && ip.label.toLowerCase().includes(q)) ||
        (ip.category && ip.category.toLowerCase().includes(q)) ||
        (ip.addedBy && ip.addedBy.toLowerCase().includes(q))
    );
  }, [serviceIPs, filterQuery]);

  // Reset to page 1 when filter changes
  useEffect(() => { setCurrentPage(1); }, [filterQuery]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredIPs.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const pagedIPs = filteredIPs.slice(startIdx, startIdx + pageSize);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!ipAddress.trim()) return;

    setAdding(true);
    setAddError(null);
    try {
      const newIP = await addServiceIP({
        ipAddress: ipAddress.trim(),
        label: label.trim(),
        category,
      });
      setServiceIPs((prev) => [...prev, newIP]);
      setIpAddress('');
      setLabel('');
      setCategory('Custom');
    } catch (err) {
      setAddError(err.message || 'Failed to add service IP');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (ip) => {
    setRemoving(true);
    try {
      await removeServiceIP(ip);
      setServiceIPs((prev) => prev.filter((item) => item.ipAddress !== ip));
      setConfirmRemove(null);
    } catch (err) {
      setError(err.message || 'Failed to remove service IP');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <ServerStackIcon className="h-7 w-7 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-100">Service IP Addresses</h1>
        </div>
        <p className="text-sm text-gray-500">
          Known Microsoft and internal service IPs that are excluded from investigation results.
        </p>
      </div>

      {/* Add Form */}
      <div className="card mb-6">
        <h2 className="text-sm font-semibold text-gray-300 mb-4">Add Service IP</h2>
        <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="IP Address (e.g. 20.190.128.0)"
              value={ipAddress}
              onChange={(e) => setIpAddress(e.target.value)}
              className="input-field font-mono text-xs"
              required
            />
          </div>
          <div className="flex-1">
            <input
              type="text"
              placeholder="Label (optional)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="input-field"
            />
          </div>
          <div className="w-full sm:w-40">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="input-field"
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={adding || !ipAddress.trim()}
            className="btn-primary flex items-center gap-2 whitespace-nowrap"
          >
            {adding ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : (
              <PlusIcon className="h-4 w-4" />
            )}
            Add
          </button>
        </form>
        {addError && (
          <p className="mt-2 text-sm text-red-400">{addError}</p>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button
            onClick={loadServiceIPs}
            className="ml-auto underline hover:no-underline text-red-400"
          >
            Retry
          </button>
        </div>
      )}

      {/* Filter and Count */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder="Filter by IP, label, category..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className="input-field pl-9"
          />
        </div>
        <span className="text-xs text-gray-500">
          {filteredIPs.length} service IP{filteredIPs.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                {['IP Address', 'Label', 'Category', 'Added By', 'Date', 'Actions'].map((label, colIdx) => (
                  <th
                    key={label}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    className={`${colIdx === 5 ? 'text-center' : 'text-left'} text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none`}
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <span className="truncate">{label}</span>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, colIdx)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg
                        className="animate-spin h-8 w-8 text-blue-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      <span className="text-gray-500 text-sm">Loading service IPs...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filteredIPs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <ServerStackIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No service IPs found</p>
                    <p className="text-gray-600 text-sm mt-1">
                      {filterQuery
                        ? 'Try adjusting your search query.'
                        : 'Add a service IP above to get started.'}
                    </p>
                  </td>
                </tr>
              )}

              {!loading &&
                pagedIPs.map((ip) => (
                  <tr
                    key={ip.ipAddress}
                    className="hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-gray-200 truncate overflow-hidden" title={ip.ipAddress}>
                      {ip.ipAddress}
                    </td>
                    <td className="px-4 py-3 text-gray-400 truncate overflow-hidden" title={ip.label || ''}>
                      {ip.label || '\u2014'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={appCategoryTone(ip.category)} dot>{ip.category}</Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={ip.addedBy || ''}>
                      {ip.addedBy || '\u2014'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs truncate overflow-hidden">
                      {formatDate(ip.addedDate)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {confirmRemove === ip.ipAddress ? (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleRemove(ip.ipAddress)}
                            disabled={removing}
                            className="btn-danger px-2 py-1 text-xs"
                          >
                            {removing ? 'Removing...' : 'Confirm'}
                          </button>
                          <button
                            onClick={() => setConfirmRemove(null)}
                            className="btn-secondary px-2 py-1 text-xs"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmRemove(ip.ipAddress)}
                          className="text-gray-600 hover:text-red-400 transition-colors p-1"
                          title="Remove service IP"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {!loading && filteredIPs.length > 0 && (
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filteredIPs.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      </div>
    </div>
  );
}

